import {
  withErrorBoundary,
  getTranslatedText,
  DataReportConstraint,
  validateForm,
  preFormatForm,
  useActivityTracker
} from '@nile/components';
import DataReportHeader from '@nile/reports/src/modules/DataReports/components/subpages/DataReportHeader';
import DataReportConfig from '@nile/reports/src/modules/DataReports/components/subpages/DataReportConfig';
import DataReportDetail from '@nile/reports/src/modules/DataReports/components/subpages/DataReportDetail';
import { DataReportsContextProvider } from '@nile/reports/src/modules/DataReports/contexts';
import { cloneDeep, isEqual, pick } from 'lodash';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { DATA_REPORT } from '@nile/reports/src/modules/home/constants';
import { Wrapper } from '@nile/reports/src/modules/DataReports/components/subpages/DataReportSheet/style';
import { SVGIcon } from '@superops/super-icons';

const getContextValue = (data, intl) => {
  return {
    reportId: data?.reportId || null,
    groupBy: data?.groupBy || null,
    entity: data?.entity || 'Ticket',
    systemChart: data?.systemChart || false,
    reportType: data?.reportType || 'DATA',
    selectAttributes: data?.selectAttributes || [],
    dataCondition: data?.dataCondition || null
  };
};

const pickedVariable = [
  'reportId',
  'name',
  'entity',
  'description',
  'groupBy',
  'reportType',
  'selectAttributes',
  'dataCondition'
];

class DataReportSheet extends Component {
  constructor(props) {
    super(props);
    this.state = {
      oldProps: props,
      reportData: getContextValue(props.data, props.intl),
      reportHeaders: {
        name:
          props?.data?.name ||
          getTranslatedText(props?.intl, 'reports.datareport.title.untitled'),
        description:
          props?.data?.description ||
          getTranslatedText(
            props?.intl,
            'reports.datareport.description.untitled'
          )
      },
      loading: false,
      submitActionLoading: false,
      hideSave: props.hideSave || false,
      edited: false,
      errors: null,
      isFullScreen: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    const pickedState =
      state.oldProps.data && pick(state.oldProps.data, pickedVariable);
    const pickedProps = props.data && pick(props.data, pickedVariable);

    if (!isEqual(pickedProps, pickedState)) {
      return {
        ...state,
        oldProps: props,
        reportData: getContextValue(props.data, props.intl)
      };
    }
  }

  updateState = (attr, value) => {
    this.setState((prevState) => ({
      ...prevState,
      [attr]: value
    }));
  };

  isUpdated = () => {
    const dataUpdated = getContextValue(
      cloneDeep(this.props.data),
      this.props.intl
    );
    const originalData = pick(dataUpdated, [
      'reportId',
      'entity',
      'groupBy',
      'selectedAttributes',
      'dataCondition',
      'name',
      'description'
    ]);

    const currentData = pick(cloneDeep(this.state.reportData), [
      'reportId',
      'entity',
      'groupBy',
      'selectedAttributes',
      'dataCondition',
      'name',
      'description'
    ]);
    const updated = isEqual(originalData, currentData);
    return updated;
  };

  isSameData = () => {
    const originalData = pick(cloneDeep(this.props.data), [
      'reportId',
      'entity',
      'groupBy',
      'selectAttributes',
      'dataCondition'
    ]);
    const currentData = pick(cloneDeep(this.state.reportData), [
      'reportId',
      'entity',
      'groupBy',
      'selectAttributes',
      'dataCondition'
    ]);
    const same = isEqual(originalData, currentData);
    return same;
  };

  validateFormData = () => {
    const updatedReportData = cloneDeep({
      ...this.state.reportHeaders,
      ...this.state.reportData
    });
    const validationResult = validateForm(
      {
        ...preFormatForm(updatedReportData),
        dataCondition: updatedReportData?.dataCondition || null
      },
      DataReportConstraint
    );
    this.setState((prevState) => ({
      ...prevState,
      errors: validationResult || null
    }));
    return validationResult || false;
  };

  handleSave = async () => {
    let updatedReportData = cloneDeep({
      ...this.state.reportHeaders,
      ...this.state.reportData
    });
    if (this.props.reportsContext?.cloneId) {
      useActivityTracker({
        event: 'DEFAULT_DATA_REPORT_CLONE',
        data: {
          reportId: this.props.reportsContext?.cloneId,
          reportName: updatedReportData?.name,
          type: DATA_REPORT
        }
      });
      updatedReportData = { ...updatedReportData, reportId: null };
    }
    useActivityTracker({
      event: 'DATA_REPORT_CREATION',
      data: {
        reportId: this.props.reportsContext?.cloneId,
        reportName: updatedReportData?.name,
        type: DATA_REPORT
      }
    });

    const validationResult = await this.validateFormData();
    if (validationResult) {
      return;
    }
    this.updateState('submitActionLoading', true);
    const variable = updatedReportData.reportId
      ? {
          reportId: updatedReportData.reportId,
          input: pick(updatedReportData, pickedVariable)
        }
      : { input: pick(updatedReportData, pickedVariable) };

    if (updatedReportData?.reportId) {
      this.props
        .updateReport({
          variables: variable
        })
        .finally((res) => {
          this.updateState('submitActionLoading', false);
        });
    } else {
      this.props
        .createNewReport({
          variables: variable
        })
        .finally((res) => {
          this.updateState('submitActionLoading', false);
        });
    }
  };

  render() {
    const {
      reportData,
      submitActionLoading,
      loading,
      hideSave,
      errors,
      reportHeaders,
      isFullScreen
    } = this.state;

    const { data, closeModal, meta } = this.props;

    const providerValue = {
      value: reportData,
      headerValue: reportHeaders,
      updateContext: (value) => this.updateState('reportData', value),
      updateHeaderValue: (value) => this.updateState('reportHeaders', value),
      isUpdated: this.isUpdated,
      setLoading: (value) => this.updateState('loading', value)
    };

    return (
      <Wrapper className="full-height" isFullScreen={isFullScreen}>
        <DataReportsContextProvider value={providerValue}>
          <DataReportHeader
            handleSave={this.handleSave}
            originalData={data}
            submitActionLoading={submitActionLoading}
            errors={errors}
            hideSave={hideSave || false}
            closeModal={closeModal}
            hideActions
          />
          <div className="dataReportSection">
            <div className="fx fh configurationSection g10">
              <div className="fh configSection">
                <DataReportConfig
                  data={reportData}
                  updateData={this.updateState}
                  allEntities={meta}
                  meta={meta}
                  errors={errors}
                  metaLoading={loading}
                />
              </div>
              <div className="minimizer">
                <div
                  className="minimizeBtn fx jctr actr"
                  onClick={() =>
                    this.updateState('isFullScreen', !isFullScreen)
                  }
                >
                  <SVGIcon
                    name={isFullScreen ? 'ArrowRight' : 'ArrowLeft'}
                    size="lg"
                  />
                </div>
              </div>
              <div className="dataSection">
                <DataReportDetail
                  data={reportData}
                  originalData={this.props.data}
                  isSameData={this.isSameData}
                  validateFormData={this.validateFormData}
                  meta={meta}
                />
              </div>
            </div>
          </div>
        </DataReportsContextProvider>
      </Wrapper>
    );
  }
}

export default injectIntl(withErrorBoundary(DataReportSheet));
